import { useStores } from "../../stores/root-store";
import { observer } from "mobx-react";
import _, { isEmpty, toNumber } from "lodash";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  currentDateString,
  formatDate,
  generateVoucherCode,
  getEnumOptions,
  ShowAlert,
} from "../../utils/functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import { ApiResponse, IModal, SelectOption } from "../../stores";
import DatePicker from "react-datepicker";
import "../../date-picker-custom.css";
import Select, { ActionMeta, MultiValue, SingleValue } from "react-select";
import { IVoucher, VoucherDiscountType } from "../../stores/voucher/index.d";
import moment from "moment";

const VoucherForm = observer(({ show, setShowModal }: IModal) => {
  const { voucherStore, productStore } = useStores();
  const { products, fetchProducts } = productStore;
  const { saveVoucher, editVoucher, fetchVouchers } = voucherStore;

  const isEditRequest = editVoucher != null && editVoucher.voucherId > 0;

  const [voucher, setVoucher] = useState<IVoucher>(
    isEditRequest
      ? editVoucher
      : {
          voucherId: 0,
          voucherCode: "",
          minOrderValue: 0,
          discountType: 0,
          discountValue: 0,
          startDate: currentDateString(),
          endDate: currentDateString(),
          maxUsesTotal: undefined,
          maxUsesPerUser: undefined,
          productIds: [],
        }
  );

  const [isFormValid, setIsFormValid] = useState(false);

  const voucherTypeList = getEnumOptions(VoucherDiscountType) as SelectOption[];

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const valid =
      !!voucher.voucherCode &&
      voucher.minOrderValue > 0 &&
      voucher.discountType > 0 &&
      (voucher.discountType == VoucherDiscountType.FreeDelivery ||
        voucher.discountValue > 0) &&
      !isEmpty(voucher.startDate) &&
      voucher.startDate != null &&
      !isEmpty(voucher.endDate) &&
      voucher.endDate != null;
    setIsFormValid(valid);
  }, [voucher]);

  const productOpts = useMemo(() => {
    const opts =
      products?.map((a) => ({
        label: a.productName,
        value: a.productId.toString(),
      })) ?? [];

    return [
      { label: "All Products", value: "0" }, // default option
      ...opts,
    ];
  }, [products]);

  const mapProductOption = (ids?: number[]) => {
    if (!ids) return [];
    return productOpts.filter((p) => ids.some((i) => i.toString() === p.value));
  };

  const mapVoucherTypeOption = (id: number) => {
    return voucherTypeList.find((p) => p.value == id.toString());
  };

  const handleMultiSelectChange = (
    evt: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    const { name } = actionMeta;
    if (name) {
      const selectedValues = evt.map((option) => option.value);
      setVoucher((prev) => ({ ...prev, [name]: selectedValues }));
    }
  };

  const handleSelectChange = (
    evt: SingleValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    const { name } = actionMeta;
    if (name) setVoucher((prev) => ({ ...prev, [name]: evt?.value }));
  };

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = evt.target;
    setVoucher((prev) => ({ ...prev, [id]: value }));
  };

  const handleDateChange = (dt: Date | null, name: string) => {
    setVoucher((prev) => ({
      ...prev,
      [name]: dt ? formatDate(dt, "YYYY-MM-DD") : "",
    }));
  };

  const submitCallback = useCallback((res: ApiResponse<number>) => {
    if (res && res.isSuccess) {
      setShowModal(false);
      fetchVouchers();
      ShowAlert.Success(
        `Voucher ${editVoucher?.id ? "updated" : "added"} successfully.`
      );
    } else {
      ShowAlert.Error(res?.message ?? "Request failed. Please try again.");
    }
  }, []);

  const handleFormSubmit = () => {
    if (!isFormValid) {
      ShowAlert.Error("One or more field value missing.");
      return;
    }

    saveVoucher(voucher, submitCallback);
  };

  const handleGenerateCode = () => {
    var code = generateVoucherCode();
    setVoucher((prev) => ({ ...prev, ["voucherCode"]: code }));
  };

  return (
    <Modal show={show} onHide={() => setShowModal(false)} className="modal-lg">
      <ModalHeader closeButton>
        <Modal.Title>
          <h5>{isEditRequest ? "Update" : "Add"} Voucher</h5>
        </Modal.Title>
      </ModalHeader>
      <ModalBody>
        <form>
          <div className="row">
            <div className="col">
              <div className="position-relative mb-3">
                <div className="form-floating mb-3">
                  <input
                    type="text"
                    className="form-control pe-5"
                    id="voucherCode"
                    placeholder="Voucher Code"
                    value={voucher.voucherCode}
                    onChange={handleInputChange}
                  />

                  <button
                    type="button"
                    className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0 text-secondary opacity-75 hover-opacity-100"
                    style={{ textDecoration: "none" }}
                    onClick={handleGenerateCode}
                  >
                    Generate
                  </button>
                  <label htmlFor="voucherCode" style={{ zIndex: 0 }}>
                    Voucher Code
                  </label>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="input-group mb-3">
                <span className="input-group-text">£</span>
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="minOrderValue"
                    placeholder="minOrderValue"
                    value={voucher.minOrderValue}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="minOrderValue" style={{ zIndex: 0 }}>
                    Minium Order Value
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col mb-3">
              <label>Discount type</label>
              <Select
                defaultValue={mapVoucherTypeOption(voucher.discountType ?? 0)}
                name="discountType"
                options={voucherTypeList}
                className="basic-select"
                classNamePrefix="select"
                onChange={handleSelectChange}
              />
            </div>
          </div>
          {voucher.discountType > 0 &&
            voucher.discountType != VoucherDiscountType.FreeDelivery && (
              <div className="input-group mb-3">
                <span className="input-group-text">
                  {voucher.discountType === VoucherDiscountType.Percentage
                    ? "%"
                    : "£"}
                </span>
                <div className="form-floating">
                  <input
                    type="number"
                    className="form-control"
                    id="discountValue"
                    placeholder="Discount Value"
                    value={voucher.discountValue}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="discountValue" style={{ zIndex: 0 }}>
                    Discount{" "}
                    {voucher.discountType === VoucherDiscountType.Percentage
                      ? "percentage"
                      : "amount"}
                  </label>
                </div>
              </div>
            )}
          <div className="row">
            <label>Voucher duration</label>
            <div className="col">
              <div className="form-floating mb-3">
                <DatePicker
                  selected={voucher.startDate as unknown as Date}
                  onChange={(date) => handleDateChange(date, "startDate")}
                  minDate={moment().toDate()}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Start Date"
                  className="form-control"
                  wrapperClassName="d-block"
                />
              </div>
            </div>
            <div className="col">
              <div className="form-floating mb-3">
                <DatePicker
                  selected={voucher.endDate as unknown as Date}
                  onChange={(date) => handleDateChange(date, "endDate")}
                  minDate={moment().toDate()}
                  dateFormat="dd MMM yyyy"
                  placeholderText="End Date"
                  className="form-control"
                  wrapperClassName="d-block"
                />
              </div>
            </div>
          </div>

          <div className="row">
            <label>Usage limitations</label>
            <div className="col">
              <div className="form-floating mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="maxUsesTotal"
                  placeholder="maxUsesTotal"
                  value={voucher.maxUsesTotal}
                  onChange={handleInputChange}
                />
                <label htmlFor="voucherCode" style={{ zIndex: 0 }}>
                  Total Usage Limit
                </label>
              </div>
            </div>
            <div className="col">
              <div className="form-floating mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="maxUsesPerUser"
                  placeholder="maxUsesPerUser"
                  value={voucher.maxUsesPerUser}
                  onChange={handleInputChange}
                />
                <label htmlFor="voucherCode" style={{ zIndex: 0 }}>
                  Per User Limit
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col mb-1">
              <label>Eligible Products (Optional)</label>
              <Select
                defaultValue={mapProductOption(voucher.productIds)}
                name="productIds"
                options={productOpts}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Choose products"
                onChange={handleMultiSelectChange}
                isMulti={true}
              />
            </div>
          </div>
          <div className="text-info">
            * The voucher code applies to all products unless specific products
            are selected.
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-default flex-fill"
          onClick={handleFormSubmit}
          disabled={!isFormValid}
        >
          {isEditRequest ? (
            <>
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              <span>Update Voucher</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              <span>Add Voucher</span>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
});

export default VoucherForm;
